<template>
  <v-container id="openingbalances" fluid tag="section">
    <div class="text-right">
      <v-btn color="cyan" @click="showFilter = !showFilter" small>
        <v-icon>mdi-filter</v-icon>
      </v-btn>
      <v-menu offset-y>
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" color="light-blue" small>
            <h3>
              {{ $i18n.t("Action") }}
              <v-icon>mdi-menu-down</v-icon>
            </h3>
          </v-btn>
        </template>
        <v-list>
          <v-list-item :to="'/openingbalance/create'">
            <v-icon>mdi-plus</v-icon>
            <v-list-item-title>{{ $i18n.t("Add New") }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <v-row justify="center" v-if="showFilter">
      <v-col cols="12" md="12">
        <base-material-card
          color="light-blue"
          icon="mdi-filter"
          class="px-3 py-1 small-icon"
          v-if="showFilter"
        >
          <div class="filter-close">
            <v-btn
              color="black"
              class="mr-0"
              fab
              small
              text
              @click="showFilter = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-row>
            <v-col cols="12" sm="3" md="2" lg="2">
              <v-autocomplete
                :label="$i18n.t('Year')"
                v-model="filter.year"
                :items="[2019, 2020, 2021, 2022, 2023, 2024, 2025]"
              />
            </v-col>
            <v-col cols="12" sm="12" md="4" lg="3">
              <z-client-select withSelf clearable v-model="filter.client_id" />
            </v-col>
            <v-col cols="12" sm="12" md="3" lg="2">
              <z-currency-select v-model="filter.currency" clearable />
            </v-col>
            <v-col cols="12" sm="12" md="3" lg="2">
              <v-text-field
                :label="$i18n.t('Balance')"
                v-model="filter.balance"
                clearable
                type="number"
              />
            </v-col>

            <v-col cols="12" sm="12" md="3" lg="2" class="text-right">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    class="mr-0"
                    color="grey darken"
                    fab
                    text
                    small
                    @click="clear(1)"
                    style="margin-right: 10px !important"
                    v-on="on"
                  >
                    <v-icon>mdi-cached</v-icon>
                  </v-btn>
                </template>
                <span>{{ $i18n.t("Clear Filter") }}</span>
              </v-tooltip>
              <v-btn
                color="success"
                class="mr-0"
                fab
                small
                dark
                @click="search()"
                :loading="loading"
              >
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </base-material-card>
      </v-col>
    </v-row>

    <v-btn
      v-if="grid != undefined && !grid.records"
      class="text-center secondary--text pa-10"
      text
      color="primary"
      style="width: 100%"
      :loading="loading"
      >{{ $i18n.t("No records available") }}</v-btn
    >

    <base-material-card
      icon="mdi-card-text-outline"
      :title="$i18n.t('Opening Balances')"
      class="px-5 py-3 small-icon"
      v-if="grid.records"
    >
      <z-grid-record :grid="grid" />

      <v-simple-table dense>
        <template v-slot:default>
          <thead class="thead">
            <tr>
              <th class="text-left">
                {{ $i18n.t("Client") }}
              </th>
              <th class="text-left">
                {{ $i18n.t("Client Entity") }}
              </th>
              <th class="text-left">
                {{ $i18n.t("Year") }}
              </th>
              <th class="text-left">
                {{ $i18n.t("Balance") }}
              </th>
              <th class="text-right">{{ $i18n.t("Action") }}</th>
            </tr>
          </thead>
          <opening-balance-row
            v-for="item in grid.rows"
            :key="item._id"
            :value="item"
            @refresh="refresh"
          />
        </template>
      </v-simple-table>

      <v-container>
        <v-pagination
          v-model="grid.page"
          class="my-4"
          :length="grid.total"
          total-visible="10"
          @input="showPage"
        ></v-pagination>
      </v-container>
    </base-material-card>
  </v-container>
</template>

<script>
import { Vue, Component, Watch } from "vue-property-decorator";
import { doGet } from "@/store/common/module/apis";
import common from "@/store/common/module/common";
import OpeningBalanceRow from "@/views/openingbalance/OpeningBalanceRow.vue";

@Component({
  name: "OpeningBalances",
  components: { OpeningBalanceRow },
})
export default class OpeningBalances extends Vue {
  grid = { page: 0, rows: [] };
  url = "/api/openingbalance/grid?&";
  showFilter = false;
  filterUrl = "";
  on = null;
  page = 1;
  rows = 50;

  filter = {
    year: null,
    client_id: null,
    currency: null,
    balance: null,
  };

  get loading() {
    return common.loading;
  }

  async mounted() {
    this.showPage(1);
  }

  async showPage(page) {
    common.setLoading(true);
    this.page = page;
    try {
      const grid = await doGet(
        this.url + "rows=" + this.rows + "&page=" + page + this.filterUrl
      );
      this.grid = grid;
    } catch (e) {
      common.setError(e.message);
    }
    common.setLoading(false);
  }

  async clear() {
    this.filter = {
      year: null,
      client_id: null,
      currency: null,
      balance: null,
    };

    this.page = 1;
    this.search();
  }

  @Watch("value")
  async refresh(value) {
    if (value == true || value == "true") {
      this.showPage(this.page);
    }
  }

  async search() {
    this.filterUrl = "";
    if (this.filter.client_id)
      this.filterUrl += "&client_id=" + this.filter.client_id;
    if (this.filter.currency)
      this.filterUrl += "&currency=" + this.filter.currency;
    if (this.filter.balance) this.filterUrl += "&amount=" + this.filter.balance;
    if (this.filter.year) this.filterUrl += "&yearMonth=" + this.filter.year;
    common.setLoading(true);
    await this.showPage(1);
  }

  isSet(_key) {
    if (
      _key != null &&
      _key != undefined &&
      _key != "null" &&
      _key != "undefined" &&
      _key != ""
    ) {
      return true;
    }
    return false;
  }
}
</script>

<style scoped>
.filter-close {
  position: absolute;
  right: 5px;
  top: 5px;
}
.dialog {
  position: fixed;
  top: 100px;
  width: 60%;
  z-index: 10;
  margin-left: 15%;
}
.sticky-bottom {
  position: sticky;
  bottom: 0;
  background-color: white;
  border-top: 1px solid #dfdfdf;
}
.sticky-top {
  position: sticky;
  top: 0;
  z-index: 2;
}
</style>
